import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Sidebar from '@/components/Sidebar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Star, Plus, Search, Trash2, Edit, Calendar, User, Tag, ChevronDown, ChevronRight, BookOpen, Image, X, Upload, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SupportFeaturesPage({ user, onLogout, isEmployee, permissions, employeeInfo }) {
  const [features, setFeatures] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingFeature, setEditingFeature] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCategories, setExpandedCategories] = useState({});
  const [expandedFeatures, setExpandedFeatures] = useState({});
  const [uploadingImages, setUploadingImages] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category_id: '',
    images: []
  });

  const isPlatformAdmin = user?.is_platform_admin;

  useEffect(() => {
    fetchData();
  }, [searchQuery]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      
      const [featuresRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/support/features?${params}`),
        axios.get(`${API}/support/categories`)
      ]);
      setFeatures(featuresRes.data);
      setCategories(categoriesRes.data);
      
      // Auto-expand all categories by default
      const expanded = {};
      categoriesRes.data.forEach(cat => {
        expanded[cat.id] = true;
      });
      expanded['uncategorized'] = true;
      setExpandedCategories(expanded);
    } catch (error) {
      toast.error('Failed to load features');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    setUploadingImages(true);
    const newImages = [...formData.images];
    
    for (const file of files) {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      try {
        const response = await axios.post(`${API}/upload`, formDataUpload, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        newImages.push(response.data.url);
      } catch (error) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    
    setFormData({ ...formData, images: newImages });
    setUploadingImages(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeImage = (index) => {
    const newImages = formData.images.filter((_, i) => i !== index);
    setFormData({ ...formData, images: newImages });
  };

  const handleSubmit = async () => {
    if (!formData.title.trim() || !formData.description.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      if (editingFeature) {
        await axios.put(`${API}/support/features/${editingFeature.id}`, formData);
        toast.success('Feature updated');
      } else {
        await axios.post(`${API}/support/features`, formData);
        toast.success('Feature created');
      }
      setShowDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save feature');
    }
  };

  const handleEdit = (feature) => {
    setEditingFeature(feature);
    setFormData({
      title: feature.title,
      description: feature.description,
      category_id: feature.category_id || '',
      images: feature.images || []
    });
    setShowDialog(true);
  };

  const handleDelete = async (featureId) => {
    if (!window.confirm('Are you sure you want to delete this feature?')) return;
    
    try {
      await axios.delete(`${API}/support/features/${featureId}`);
      toast.success('Feature deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete feature');
    }
  };

  const resetForm = () => {
    setFormData({ title: '', description: '', category_id: '', images: [] });
    setEditingFeature(null);
  };

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  const toggleFeature = (featureId) => {
    setExpandedFeatures(prev => ({
      ...prev,
      [featureId]: !prev[featureId]
    }));
  };

  const scrollToCategory = (categoryId) => {
    const element = document.getElementById(`category-${categoryId}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setExpandedCategories(prev => ({ ...prev, [categoryId]: true }));
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getCategoryColor = (categoryId) => {
    const cat = categories.find(c => c.id === categoryId);
    return cat?.color || '#6366f1';
  };

  // Group features by category
  const featuresByCategory = () => {
    const grouped = {};
    
    // Initialize with all categories
    categories.forEach(cat => {
      grouped[cat.id] = {
        category: cat,
        features: []
      };
    });
    
    // Add uncategorized
    grouped['uncategorized'] = {
      category: { id: 'uncategorized', name: 'Uncategorized', color: '#6b7280' },
      features: []
    };
    
    // Group features
    features.forEach(feature => {
      const catId = feature.category_id || 'uncategorized';
      if (grouped[catId]) {
        grouped[catId].features.push(feature);
      } else {
        grouped['uncategorized'].features.push(feature);
      }
    });
    
    // Filter out empty categories (except when searching)
    if (!searchQuery) {
      return Object.values(grouped).filter(g => g.features.length > 0);
    }
    return Object.values(grouped).filter(g => g.features.length > 0);
  };

  const groupedFeatures = featuresByCategory();

  return (
    <div className="flex h-screen bg-background">
      <Sidebar user={user} onLogout={onLogout} isEmployee={isEmployee} permissions={permissions} employeeInfo={employeeInfo} />
      <div className="flex-1 overflow-auto">
        <div className="p-6 max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-heading font-bold flex items-center gap-3">
                <BookOpen className="w-8 h-8 text-yellow-500" />
                Platform Manual
              </h1>
              <p className="text-muted-foreground mt-1">Features and capabilities documentation</p>
            </div>
            {isPlatformAdmin && (
              <Button onClick={() => { resetForm(); setShowDialog(true); }} className="bg-yellow-500 text-black hover:bg-yellow-400" data-testid="add-feature-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Feature
              </Button>
            )}
          </div>

          {/* Search */}
          <div className="mb-6">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search features..."
                className="pl-10 bg-muted/30"
              />
            </div>
          </div>

          <div className="flex gap-6">
            {/* Table of Contents Sidebar */}
            <div className="w-64 flex-shrink-0">
              <Card className="bg-card border-border sticky top-6">
                <CardContent className="p-4">
                  <h2 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground mb-3">
                    Contents
                  </h2>
                  <nav className="space-y-1">
                    {groupedFeatures.map(group => (
                      <button
                        key={group.category.id}
                        onClick={() => scrollToCategory(group.category.id)}
                        className="w-full text-left px-3 py-2 rounded-lg text-sm hover:bg-muted/50 transition-colors flex items-center gap-2"
                        style={{ borderLeft: `3px solid ${group.category.color}` }}
                      >
                        <span className="flex-1 truncate">{group.category.name}</span>
                        <span className="text-xs text-muted-foreground bg-muted/50 px-1.5 py-0.5 rounded">
                          {group.features.length}
                        </span>
                      </button>
                    ))}
                  </nav>
                </CardContent>
              </Card>
            </div>

            {/* Main Content */}
            <div className="flex-1 min-w-0">
              {loading ? (
                <div className="text-center py-12 text-muted-foreground">Loading...</div>
              ) : groupedFeatures.length === 0 ? (
                <Card className="bg-card border-border">
                  <CardContent className="py-12 text-center">
                    <Star className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">No features found</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-6">
                  {groupedFeatures.map(group => (
                    <div key={group.category.id} id={`category-${group.category.id}`}>
                      {/* Category Header - Collapsible */}
                      <button
                        onClick={() => toggleCategory(group.category.id)}
                        className="w-full flex items-center gap-3 p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors mb-3"
                      >
                        {expandedCategories[group.category.id] ? (
                          <ChevronDown className="w-5 h-5 text-muted-foreground" />
                        ) : (
                          <ChevronRight className="w-5 h-5 text-muted-foreground" />
                        )}
                        <div 
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: group.category.color }}
                        />
                        <h2 className="text-lg font-semibold flex-1 text-left">{group.category.name}</h2>
                        <span className="text-sm text-muted-foreground">
                          {group.features.length} feature{group.features.length !== 1 ? 's' : ''}
                        </span>
                      </button>

                      {/* Features List - Collapsible */}
                      {expandedCategories[group.category.id] && (
                        <div className="space-y-2 pl-4 border-l-2 ml-2" style={{ borderColor: group.category.color + '40' }}>
                          {group.features.map(feature => (
                            <Card 
                              key={feature.id} 
                              className="bg-card border-border hover:border-yellow-500/30 transition-colors"
                            >
                              <CardContent className="p-0">
                                {/* Feature Title - Clickable to expand */}
                                <button
                                  onClick={() => toggleFeature(feature.id)}
                                  className="w-full flex items-center gap-3 p-4 text-left hover:bg-muted/20 transition-colors"
                                >
                                  {expandedFeatures[feature.id] ? (
                                    <ChevronDown className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                                  ) : (
                                    <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                                  )}
                                  <Star className="w-4 h-4 text-yellow-500 flex-shrink-0" />
                                  <span className="font-medium flex-1">{feature.title}</span>
                                  {feature.images?.length > 0 && (
                                    <span className="flex items-center gap-1 text-xs text-muted-foreground">
                                      <Image className="w-3 h-3" />
                                      {feature.images.length}
                                    </span>
                                  )}
                                </button>

                                {/* Feature Content - Expandable */}
                                {expandedFeatures[feature.id] && (
                                  <div className="px-4 pb-4 pt-0 border-t border-border/50">
                                    <p className="text-muted-foreground whitespace-pre-wrap mt-3">
                                      {feature.description}
                                    </p>
                                    
                                    {/* Feature Images */}
                                    {feature.images?.length > 0 && (
                                      <div className="mt-4 grid grid-cols-2 gap-3">
                                        {feature.images.map((img, idx) => (
                                          <button 
                                            key={idx}
                                            type="button"
                                            onClick={(e) => { e.stopPropagation(); setPreviewImage(img); }}
                                            className="block rounded-lg overflow-hidden border border-border hover:border-yellow-500/50 transition-colors cursor-zoom-in"
                                          >
                                            <img 
                                              src={img} 
                                              alt={`${feature.title} screenshot ${idx + 1}`}
                                              className="w-full h-40 object-cover"
                                            />
                                          </button>
                                        ))}
                                      </div>
                                    )}
                                    
                                    <div className="flex items-center justify-between mt-4 pt-3 border-t border-border/50">
                                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                                        <span className="flex items-center gap-1">
                                          <User className="w-3 h-3" />
                                          {feature.created_by_name}
                                        </span>
                                        <span className="flex items-center gap-1">
                                          <Calendar className="w-3 h-3" />
                                          {formatDate(feature.created_at)}
                                        </span>
                                      </div>
                                      {isPlatformAdmin && (
                                        <div className="flex items-center gap-1">
                                          <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleEdit(feature); }}>
                                            <Edit className="w-4 h-4" />
                                          </Button>
                                          <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleDelete(feature.id); }} className="text-red-400 hover:text-red-300">
                                            <Trash2 className="w-4 h-4" />
                                          </Button>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Add/Edit Dialog */}
          <Dialog open={showDialog} onOpenChange={setShowDialog}>
            <DialogContent className="bg-card border-border max-w-2xl">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Star className="w-5 h-5 text-yellow-500" />
                  {editingFeature ? 'Edit Feature' : 'Add Feature'}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
                <div className="space-y-2">
                  <Label>Title *</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Feature title"
                    className="bg-muted/30"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Description *</Label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Describe this feature in detail..."
                    className="w-full h-32 px-3 py-2 bg-muted/30 border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-yellow-500/50 resize-none"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Category</Label>
                  <Select value={formData.category_id || "none"} onValueChange={(v) => setFormData({ ...formData, category_id: v === "none" ? "" : v })}>
                    <SelectTrigger className="bg-muted/30">
                      <SelectValue placeholder="Select category (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No Category</SelectItem>
                      {categories.map(cat => (
                        <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Image Upload Section */}
                <div className="space-y-2">
                  <Label>Images</Label>
                  <div className="space-y-3">
                    {/* Existing Images */}
                    {formData.images.length > 0 && (
                      <div className="grid grid-cols-3 gap-2">
                        {formData.images.map((img, idx) => (
                          <div key={idx} className="relative group">
                            <img 
                              src={img} 
                              alt={`Upload ${idx + 1}`}
                              className="w-full h-24 object-cover rounded-lg border border-border"
                            />
                            <button
                              type="button"
                              onClick={() => removeImage(idx)}
                              className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Upload Button */}
                    <div>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleImageUpload}
                        className="hidden"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploadingImages}
                        className="w-full border-dashed"
                      >
                        {uploadingImages ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Uploading...
                          </>
                        ) : (
                          <>
                            <Upload className="w-4 h-4 mr-2" />
                            Upload Images
                          </>
                        )}
                      </Button>
                      <p className="text-xs text-muted-foreground mt-1">
                        Upload screenshots or images to illustrate this feature
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
                <Button onClick={handleSubmit} className="bg-yellow-500 text-black hover:bg-yellow-400" data-testid="submit-feature-btn">
                  {editingFeature ? 'Update' : 'Create'} Feature
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Image Preview Dialog */}
          <Dialog open={!!previewImage} onOpenChange={() => setPreviewImage(null)}>
            <DialogContent className="bg-card border-border max-w-4xl max-h-[90vh] p-0 overflow-hidden">
              <DialogHeader className="p-4 pb-2">
                <DialogTitle className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Image className="w-5 h-5 text-yellow-500" />
                    Image Preview
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setPreviewImage(null)}
                    className="h-8 w-8 p-0"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </DialogTitle>
              </DialogHeader>
              <div className="p-4 pt-0 flex items-center justify-center max-h-[calc(90vh-80px)] overflow-auto">
                {previewImage && (
                  <img 
                    src={previewImage} 
                    alt="Preview"
                    className="max-w-full max-h-full object-contain rounded-lg"
                    style={{ maxHeight: 'calc(90vh - 120px)' }}
                  />
                )}
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </div>
  );
}
